import memoize from "lodash/memoize";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import type { BillableTool } from "~api/BillableTool.ts";
import { useAllEmployees } from "~api/employees";
import { useBillableTools } from "~api/tools";
import { CurrentTenantContext } from "~contexts/CurrentTenantContext/CurrentTenantContext.tsx";
import type { Employee } from "~generated";
import { hashCode } from "~lib/hashCode";
import type { Resource } from "~lib/resourceHelpers.ts";
import {
  employeeToResource,
  ResourceType,
  toolToResource,
} from "~lib/resourceHelpers.ts";
import {
  createResourceMap,
  findResourceInMap,
  getResourceNameFromMap,
  type ResourceCacheEntry,
} from "./resourceMapHelpers.ts";

interface ResourceContextValue {
  resourceMap: Map<string, ResourceCacheEntry>;
  getResource: (id: string | null | undefined) => Resource | undefined;
  getResourceName: (id: string | null | undefined) => string;
  resourceNames: Record<string, string>;
  allResources: Resource[];
  activeResources: Resource[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
}

const ResourceContext = createContext<ResourceContextValue | undefined>(
  undefined,
);

interface ResourceProviderProps {
  children: ReactNode;
}

export function ResourceProvider({ children }: ResourceProviderProps) {
  const { resources, employees, tools, isLoading, isError, isFetching } =
    useAllResources();

  const resourceMap = useMemo(() => createResourceMap(resources), [resources]);

  const resourceNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const [id, entry] of resourceMap) {
      names[id] = entry.displayName;
    }
    return names;
  }, [resourceMap]);

  const employeeMap = useMemo(() => {
    const map = new Map();
    employees?.forEach((emp) => {
      map.set(emp.id, emp);
    });
    return map;
  }, [employees]);

  const toolMap = useMemo(() => {
    const map = new Map();
    tools?.forEach((tool) => {
      map.set(tool.id, tool);
    });
    return map;
  }, [tools]);

  // Read the context directly instead of useCurrentTenant(): this provider is
  // mounted above <Routes /> (App.tsx), so it renders before RequireTenantAccess
  // sets the tenant. useCurrentTenant() throws when no tenant is set and would
  // crash on initial render / non-tenant routes. No tenant -> no filtering.
  const { currentTenant } = useContext(CurrentTenantContext);
  const currentTenantId = currentTenant?.id;

  const activeResources = useMemo(() => {
    if (!resources) return [];

    return resources.filter((resource) => {
      if (resource.type === ResourceType.EMPLOYEE) {
        const employee = employeeMap.get(resource.id);
        if (employee?.resigned_at) return false;
        // Only employees of the current tenant may be selected for time
        // tracking, construction site planning and crews (GREW-2778).
        // Tools may be shared across tenants (see
        // ForeignTenantToolWarningProvider), so they are not filtered here.
        return !currentTenantId || employee?.tenant_id === currentTenantId;
      }
      if (resource.type === ResourceType.TOOL) {
        return toolMap.get(resource.id)?.active;
      }
      return true;
    });
  }, [resources, employeeMap, toolMap, currentTenantId]);

  const value = useMemo<ResourceContextValue>(
    () => ({
      resourceMap,
      getResource: (id) => findResourceInMap(resourceMap, id),
      getResourceName: (id) => getResourceNameFromMap(resourceMap, id),
      resourceNames,
      allResources: resources || [],
      activeResources,
      isLoading,
      isError,
      isFetching,
    }),
    [
      resourceMap,
      resourceNames,
      resources,
      activeResources,
      isLoading,
      isError,
      isFetching,
    ],
  );

  return (
    <ResourceContext.Provider value={value}>
      {children}
    </ResourceContext.Provider>
  );
}

export function useResources(): ResourceContextValue {
  const context = useContext(ResourceContext);
  if (!context) {
    throw new Error("useResources must be used within ResourceProvider");
  }
  return context;
}

function useAllResources() {
  const {
    data: employees,
    isLoading: isLoadingEmployees,
    isError: isErrorEmployees,
    isFetching: isFetchingEmployees,
  } = useAllEmployees();
  const {
    data: tools,
    isLoading: isLoadingTools,
    isError: isErrorTools,
    isFetching: isFetchingTools,
  } = useBillableTools();
  const resources = buildResources(employees, tools);

  return {
    resources,
    tools: tools,
    employees: employees,
    isLoading: isLoadingEmployees || isLoadingTools,
    isError: isErrorEmployees || isErrorTools,
    isFetching: isFetchingEmployees || isFetchingTools,
  };
}

const buildResources = memoize(
  (employees?: Employee[], tools?: BillableTool[]) => [
    ...(employees || []).map(employeeToResource),
    ...(tools || []).map(toolToResource),
  ],
  (employees, tools) =>
    hashCode(JSON.stringify({ employees, tools })).toString(),
);
